import { AppRoutesPaths } from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { SimpleLockIcon } from '@/utils/helpers';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const StyledBannerCard = styled.div`
  width: 490px;
  padding: var(--medium-big-padding);
  background: var(--white-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledHoverPopover = styled(StyledBannerCard)`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100vw - 32px);
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const StyledOverlay = styled(StyledBannerCard)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const StyledTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const StyledTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  span {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }
`;

const StyledDescription = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  overflow-wrap: break-word;
`;

const BannerContent: React.FC<{ description: string }> = ({ description }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <StyledTextColumn>
        <StyledTitleRow>
          <SimpleLockIcon />
          <span>{t('user.freeTrial.limitReached.title')}</span>
        </StyledTitleRow>
        <StyledDescription>{description}</StyledDescription>
      </StyledTextColumn>
      <AppButton
        label={t('user.freeTrial.limitReached.bookMeeting')}
        onClick={() => navigate(AppRoutesPaths.HUBSPOT_BOOKING_ROUTE)}
        style={{
          borderRadius: 'var(--default-border-radius)',
          backgroundColor: 'var(--red-dark)',
          borderColor: 'var(--red-dark)',
          whiteSpace: 'nowrap',
        }}
      />
    </>
  );
};

type TrialLimitBannerProps = {
  description: string;
  children: React.ReactNode;
};

export const TrialLimitBanner: React.FC<TrialLimitBannerProps> = ({
  description,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeout = useRef(0);

  const handleMouseEnter = () => {
    window.clearTimeout(hideTimeout.current);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeout.current = window.setTimeout(() => setIsVisible(false), 100);
  };

  return (
    <StyledWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <StyledHoverPopover>
          <BannerContent description={description} />
        </StyledHoverPopover>
      )}
    </StyledWrapper>
  );
};

type TrialLimitOverlayProps = {
  description: string;
};

export const TrialLimitOverlay: React.FC<TrialLimitOverlayProps> = ({
  description,
}) => (
  <StyledOverlay>
    <BannerContent description={description} />
  </StyledOverlay>
);
